import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { FeatureFlag } from '@app/enums';
import { FeatureRedirectOptions } from '@app/interfaces';
import { AuthService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { error, redirect, replaceUrlParams } from './helpers';

async function isKycKybEnabled(authService: AuthService, featureService: FeatureService): Promise<boolean> {
    if (authService.company.hasWaveDefinition) return true;
    return featureService.has(FeatureFlag.KYC_KYB);
}

@Injectable()
/**
 * Replaces FeatureGuard for KYC_KYB routes.
 * Allows access if the company has a KycKybWave assigned, otherwise falls back to the KYC_KYB feature flag.
 */
export class KycKybFeatureGuard {
    constructor(
        private authService: AuthService,
        private featureService: FeatureService,
        private router: Router
    ) {}

    async canActivate(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Promise<boolean | UrlTree> {
        const enabled = await isKycKybEnabled(this.authService, this.featureService);
        return enabled ? true : this.router.createUrlTree(['/']);
    }
}

@Injectable()
/**
 * Replaces HasFeatureRedirectGuard for KYC_KYB routes.
 * Redirects to the route specified in feature.redirectTo if the company has KYC_KYB enabled.
 */
export class HasKycKybRedirectGuard {
    constructor(
        private authService: AuthService,
        private featureService: FeatureService,
        private router: Router
    ) {}

    async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
        const { redirectTo, redirectToReplace } = (route.data as FeatureRedirectOptions).feature || {};

        if (!redirectTo && !redirectToReplace) {
            error(state.url);
        }

        if (await isKycKybEnabled(this.authService, this.featureService)) {
            return this.router.createUrlTree([replaceUrlParams(redirectTo ?? [], route.params)]);
        }

        return true;
    }
}

@Injectable()
/**
 * Replaces DoesntHaveFeatureRedirectGuard for KYC_KYB routes.
 * Redirects to the route specified in feature.redirectTo if the company does not have KYC_KYB enabled.
 */
export class KycKybDoesntHaveRedirectGuard {
    constructor(
        private authService: AuthService,
        private featureService: FeatureService,
        private router: Router
    ) {}

    async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
        const { redirectTo, redirectToReplace } = (route.data as FeatureRedirectOptions).feature || {};

        if (!redirectTo && !redirectToReplace) {
            error(state.url);
        }

        if (!(await isKycKybEnabled(this.authService, this.featureService))) {
            redirect(this.router, route.params, redirectTo ?? []);
        }

        return true;
    }
}
